import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pause, Play, Trash2, Pencil, Tag, Gift } from "lucide-react";
import { api, apiErr, inputCls, labelCls } from "./adminShared";

const empty = { code: "", type: "fixed", value: 10, start_date: "", end_date: "", min_booking: 0,
  eligible_services: [], usage_limit: "", per_customer_limit: "", combinable: false, status: "active" };

const STATUS_STYLE = { active: "bg-green-100 text-green-800", paused: "bg-amber-100 text-amber-800",
  expired: "bg-red-100 text-red-700", scheduled: "bg-blue-100 text-blue-800" };

export default function AdminDiscounts() {
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/discounts").then((r) => setItems(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); api.get("/services?include_hidden=true").then((r) => setServices(r.data)).catch(() => {}); }, []);

  const reset = () => { setForm(empty); setEditing(null); };
  const startEdit = (d) => {
    setEditing(d.discount_id);
    setForm({ code: d.code, type: d.type, value: d.value, start_date: d.start_date || "", end_date: d.end_date || "",
      min_booking: d.min_booking || 0, eligible_services: d.eligible_services || [],
      usage_limit: d.usage_limit ?? "", per_customer_limit: d.per_customer_limit ?? "",
      combinable: !!d.combinable, status: d.status || "active" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!form.code.trim()) { toast.error("Enter a code"); return; }
    setBusy(true);
    const payload = { ...form, value: Number(form.value), min_booking: Number(form.min_booking || 0),
      usage_limit: form.usage_limit === "" ? null : Number(form.usage_limit),
      per_customer_limit: form.per_customer_limit === "" ? null : Number(form.per_customer_limit),
      start_date: form.start_date || null, end_date: form.end_date || null };
    try {
      if (editing) { await api.put(`/admin/discounts/${editing}`, payload); toast.success("Discount updated"); }
      else { await api.post("/admin/discounts", payload); toast.success("Discount created"); }
      reset(); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const toggleSvc = (slug) => setForm((f) => ({ ...f,
    eligible_services: f.eligible_services.includes(slug) ? f.eligible_services.filter((s) => s !== slug) : [...f.eligible_services, slug] }));

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiErr(e)); } };

  const manual = items.filter((d) => !d.is_birthday);
  const birthdays = items.filter((d) => d.is_birthday);

  return (
    <div className="space-y-6" data-testid="admin-discounts">
      <section className="bg-white rounded-2xl border border-[#E6DEC3] p-5">
        <h3 className="font-serif text-xl text-[#2C2621] mb-3 flex items-center gap-2"><Tag size={18} className="text-[#B87333]" /> {editing ? "Edit discount" : "Create a discount code"}</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div><label className={labelCls}>Code</label><input value={form.code} disabled={!!editing} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={inputCls} placeholder="SPRING10" data-testid="disc-code" /></div>
          <div><label className={labelCls}>Type</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls} data-testid="disc-type">
              <option value="fixed">Fixed $ off</option><option value="percent">% off</option>
            </select></div>
          <div><label className={labelCls}>{form.type === "percent" ? "Percent (%)" : "Amount ($)"}</label><input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className={inputCls} data-testid="disc-value" /></div>
          <div><label className={labelCls}>Start date</label><input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inputCls} data-testid="disc-start" /></div>
          <div><label className={labelCls}>Expiration date</label><input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inputCls} data-testid="disc-end" /></div>
          <div><label className={labelCls}>Min booking ($, before tax)</label><input type="number" value={form.min_booking} onChange={(e) => setForm({ ...form, min_booking: e.target.value })} className={inputCls} data-testid="disc-min" /></div>
          <div><label className={labelCls}>Total usage limit</label><input type="number" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} className={inputCls} placeholder="unlimited" data-testid="disc-usage" /></div>
          <div><label className={labelCls}>Per-customer limit</label><input type="number" value={form.per_customer_limit} onChange={(e) => setForm({ ...form, per_customer_limit: e.target.value })} className={inputCls} placeholder="unlimited" data-testid="disc-percust" /></div>
          <div><label className={labelCls}>Status</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls} data-testid="disc-status">
              <option value="active">Active</option><option value="paused">Paused</option>
            </select></div>
        </div>
        <div className="mt-3">
          <label className={labelCls}>Eligible services (none = all services)</label>
          <div className="mt-1 flex flex-wrap gap-2">
            {services.map((s) => (
              <button key={s.slug} onClick={() => toggleSvc(s.slug)} className={`text-xs rounded-full px-3 py-1.5 border ${form.eligible_services.includes(s.slug) ? "bg-[#B87333] text-white border-[#B87333]" : "border-[#E6DEC3] text-[#635B52]"}`} data-testid={`disc-svc-${s.slug}`}>{s.name}</button>
            ))}
          </div>
        </div>
        <label className="mt-3 flex items-center gap-2 text-sm text-[#2C2621]">
          <input type="checkbox" checked={form.combinable} onChange={(e) => setForm({ ...form, combinable: e.target.checked })} className="w-4 h-4 accent-[#B87333]" data-testid="disc-combinable" />
          Can be combined with other discounts
        </label>
        <div className="mt-4 flex gap-2">
          <button onClick={save} disabled={busy} className="inline-flex items-center gap-1 bg-[#B87333] text-white rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-60" data-testid="disc-save"><Plus size={15} /> {editing ? "Save changes" : "Create discount"}</button>
          {editing && <button onClick={reset} className="rounded-full px-4 py-2.5 text-sm border border-[#E6DEC3] text-[#635B52]" data-testid="disc-cancel">Cancel</button>}
        </div>
      </section>

      <section>
        <p className={labelCls}>Discount codes</p>
        <div className="mt-2 space-y-2" data-testid="disc-list">
          {manual.map((d) => (
            <div key={d.discount_id} className="flex flex-wrap items-center justify-between gap-2 bg-white border border-[#E6DEC3] rounded-xl p-3 text-sm">
              <div>
                <span className="font-semibold text-[#2C2621]">{d.code}</span>
                <span className="ml-2 text-[#635B52]">{d.type === "percent" ? `${d.value}% off` : `$${Number(d.value).toFixed(2)} off`}</span>
                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${STATUS_STYLE[d.effective_status] || "bg-gray-100"}`}>{d.effective_status}</span>
                <div className="text-xs text-[#635B52] mt-0.5">
                  min ${Number(d.min_booking || 0).toFixed(0)} · used {d.used_count || 0}{d.usage_limit ? `/${d.usage_limit}` : ""}
                  {d.eligible_services?.length ? ` · ${d.eligible_services.join(", ")}` : " · all services"}
                  {d.combinable ? " · combinable" : ""}{d.end_date ? ` · exp ${d.end_date}` : ""}
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => startEdit(d)} className="inline-flex items-center gap-1 border border-[#E6DEC3] rounded-full px-3 py-1.5 text-xs" data-testid={`disc-edit-${d.code}`}><Pencil size={12} /> Edit</button>
                <button onClick={() => act(() => api.post(`/admin/discounts/${d.discount_id}/toggle`), "Updated")} className="inline-flex items-center gap-1 border border-[#E6DEC3] rounded-full px-3 py-1.5 text-xs" data-testid={`disc-toggle-${d.code}`}>{d.status === "active" ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Activate</>}</button>
                <button onClick={() => { if (window.confirm(`Delete ${d.code}?`)) act(() => api.delete(`/admin/discounts/${d.discount_id}`), "Deleted"); }} className="inline-flex items-center gap-1 border border-red-300 text-red-700 rounded-full px-3 py-1.5 text-xs" data-testid={`disc-delete-${d.code}`}><Trash2 size={12} /> Delete</button>
              </div>
            </div>
          ))}
          {!manual.length && <p className="text-sm text-[#635B52]">No discount codes yet.</p>}
        </div>
      </section>

      {birthdays.length > 0 && (
        <section>
          <p className={labelCls}><Gift size={13} className="inline mr-1 text-[#B87333]" /> Auto-generated birthday codes</p>
          <div className="mt-2 space-y-2" data-testid="disc-birthday-list">
            {birthdays.map((d) => (
              <div key={d.discount_id} className="flex items-center justify-between bg-[#F5F0E8] border border-[#E6DEC3] rounded-xl p-3 text-sm">
                <span><span className="font-semibold text-[#2C2621]">{d.code}</span> · $25 off · {d.assigned_email}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLE[d.effective_status] || "bg-gray-100"}`}>{d.effective_status}{d.used_count ? " · used" : ""}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
